import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HandshakeIcon from '@mui/icons-material/Handshake';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SupportIcon from '@mui/icons-material/Support';
import {
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { Button, Container, Heading, Text } from '../components/atomic';
import { ContentPageLayout } from '../layouts';

/**
 * Join as a Services Integrator page component for RSM Collective
 *
 * Page for systems integrators and technology partners interested in partnering with RSM Collective
 *
 * @returns {JSX.Element} Join as a Services Integrator page component
 */
function JoinIntegrator() {
  const partnershipBenefits = [
    {
      icon: <HandshakeIcon />,
      title: 'Strategic Partnership',
      description:
        'Collaborate with industry leaders in digital transformation',
    },
    {
      icon: <RocketLaunchIcon />,
      title: 'Market Expansion',
      description:
        'Access new enterprise markets and AWS Marketplace opportunities',
    },
    {
      icon: <IntegrationInstructionsIcon />,
      title: 'Technical Excellence',
      description:
        'Leverage our AI and automation expertise for enhanced solutions',
    },
    {
      icon: <SupportIcon />,
      title: '$ Investment Opportunities',
      description: 'Deliver comprehensive solutions with combined expertise',
    },
  ];

  // const _idealPartners = [
  //   'Cloud infrastructure providers (AWS, Azure, GCP)',
  //   'Enterprise IT consulting firms',
  //   'Systems integration specialists',
  //   'Data analytics and BI companies',
  //   'Workflow automation providers',
  //   'Cybersecurity and compliance solution vendors',
  //   'Enterprise software platform developers',
  //   'Digital transformation consultants',
  // ];

  const collaborationModels = [
    {
      title: 'Technology Partnership',
      description:
        'Integrate your solutions with our AI-powered platform for enhanced enterprise delivery',
      features: [
        'API integration',
        'Joint solution development',
        'Technical certification',
      ],
    },
    {
      title: 'Implementation Partnership',
      description:
        'Combine implementation expertise to deliver comprehensive enterprise transformations',
      features: [
        'Joint project delivery',
        'Resource sharing',
        'Knowledge exchange',
      ],
    },
    {
      title: 'Channel Partnership',
      description:
        'Expand market reach through mutual referrals and co-selling opportunities',
      features: [
        'Lead sharing',
        'Co-marketing initiatives',
        'Revenue sharing models',
      ],
    },
    {
      title: 'Strategic Alliance',
      description:
        'Form deep partnerships for long-term enterprise innovation initiatives',
      features: ['Joint ventures', 'Co-innovation labs', 'Market development'],
    },
  ];

  return (
    <ContentPageLayout
      useHeader={true}
      title="Join as a Technology Partner"
      subtitle=""
      headerDark={false}
      backgroundColor="var(--color-background-light)"
    >
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Heading
            variant="h2"
            sx={{ mb: 3, fontSize: '2rem', fontWeight: 600 }}
          >
            Partnerships that Drive Innovation
          </Heading>
          <Text
            sx={{
              fontSize: '1.125rem',
              lineHeight: 1.7,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Join The Collective to deliver on the art of the possible. Combine
            your technology solutions with our consulting expertise to build
            differentiated offerings. We drive market adoption leveraging AWS
            Marketplace programs and sales channel.
          </Text>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 8 }}>
          <Heading
            variant="h2"
            sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Partnership Benefits
          </Heading>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {partnershipBenefits.map((benefit, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    maxWidth: '300px',
                    mx: 'auto',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    position: 'relative',
                    border: '1px solid var(--color-border)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -1,
                      left: index % 2 === 0 ? -1 : 'auto',
                      right: index % 2 === 1 ? -1 : 'auto',
                      width: '40px',
                      height: '40px',
                      borderTop: '3px solid var(--color-primary)',
                      borderLeft:
                        index % 2 === 0
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRight:
                        index % 2 === 1
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRadius: '0 !important',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      right: index % 2 === 0 ? -1 : 'auto',
                      left: index % 2 === 1 ? -1 : 'auto',
                      width: '40px',
                      height: '40px',
                      borderBottom: '3px solid var(--color-primary)',
                      borderRight:
                        index % 2 === 0
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderLeft:
                        index % 2 === 1
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRadius: '0 !important',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {React.cloneElement(benefit.icon, {
                        sx: { fontSize: 48 },
                      })}
                    </Box>
                    <Heading variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      {benefit.title}
                    </Heading>
                    <Text sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                      {benefit.description}
                    </Text>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Collaboration Models */}
        <Box sx={{ mb: 8 }}>
          <Heading
            variant="h2"
            sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Collaboration Models
          </Heading>
          <Grid container spacing={3}>
            {collaborationModels.map((model, index) => (
              <Grid item xs={12} lg={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    maxWidth: '500px',
                    mx: 'auto',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Heading
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontSize: '1.25rem',
                        color: 'primary.main',
                      }}
                    >
                      {model.title}
                    </Heading>
                    <Text sx={{ mb: 3, fontSize: '0.95rem' }}>
                      {model.description}
                    </Text>
                    <List dense>
                      {model.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon
                              sx={{ color: 'primary.main', fontSize: 20 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              sx: { fontSize: '0.9rem' },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* COMMENTED OUT - Success Stories Preview with Fake Statistics - Keep as Template for Future Use
        <Box sx={{ 
          mb: 8, 
          backgroundColor: 'white', 
          border: '1px solid #e8e8e8', 
          p: 4,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -1,
            right: -1,
            width: '40px',
            height: '40px',
            borderTop: '3px solid var(--color-primary)',
            borderRight: '3px solid var(--color-primary)',
            borderRadius: '0 !important',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -1,
            left: -1,
            width: '40px',
            height: '40px',
            borderBottom: '3px solid var(--color-primary)',
            borderLeft: '3px solid var(--color-primary)',
            borderRadius: '0 !important',
          }
        }}>
          <Heading variant="h2" sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}>
            Partnership Success
          </Heading>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box>
                <Text sx={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'primary.main' }}>
                  50+
                </Text>
                <Text sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                  Active Integration Partners
                </Text>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Text sx={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'primary.main' }}>
                  200+
                </Text>
                <Text sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                  Joint Projects Delivered
                </Text>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Text sx={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'primary.main' }}>
                  95%
                </Text>
                <Text sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                  Partner Satisfaction Rate
                </Text>
              </Box>
            </Grid>
          </Grid>
        </Box>
        END COMMENTED OUT FAKE STATISTICS TEMPLATE */}

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            backgroundColor: 'var(--color-card)',
            boxShadow: 1,
            position: 'relative',
            border: '1px solid var(--color-border)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -1,
              left: -1,
              width: '40px',
              height: '40px',
              borderTop: '3px solid var(--color-primary)',
              borderLeft: '3px solid var(--color-primary)',
              borderRadius: '0 !important',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -1,
              right: -1,
              width: '40px',
              height: '40px',
              borderBottom: '3px solid var(--color-primary)',
              borderRight: '3px solid var(--color-primary)',
              borderRadius: '0 !important',
            },
          }}
        >
          <Heading
            variant="h2"
            sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Ready to Build Something Great Together?
          </Heading>
          <Text
            sx={{
              mb: 4,
              fontSize: '1.125rem',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Join our ecosystem of technology partners and systems integrators.
            Together, we'll create solutions that redefines time to value for
            modernization projects.
          </Text>
          <Button
            variant="contained"
            size="large"
            onClick={() =>
              window.open(
                'https://www.linkedin.com/company/rsm-collective/',
                '_blank',
                'noopener,noreferrer'
              )
            }
            aria-label="Contact RSM Collective on LinkedIn (opens in new window)"
            sx={{
              backgroundColor: 'primary.main',
              color: 'var(--color-primary-text)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderTopLeftRadius: 0,
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: 0,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Let's Collaborate
          </Button>
        </Box>
      </Container>
    </ContentPageLayout>
  );
}

export default JoinIntegrator;
